import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, Tag as TagIcon, Check, Plus, Trash2, Eye } from 'lucide-react';
import { CalendarEvent } from '../../../hooks/useSupabaseEvents';
import { KanbanColumn } from '../../../hooks/useSupabaseKanban';
import { dateRangeToSelectedCells } from '../../../utils/calendar/dateHelpers';
import { useTimeFormat } from '../../settings/TimePicker';
import { InlineTimeInput, EndTimeInput } from '../dialogs/InlineTimeInput';
import { DateTextInput } from '../dialogs/DateTextInput';
import { RecurrenceRule } from '../dialogs/RecurrenceSelector';
import DatePicker from '../dialogs/DatePicker';
import { Calendar } from '../../../types';
import { Tag } from '../../../hooks/useTags';
import { useCalendar } from '../../../contexts/CalendarContext';
import { useSwipeDismiss } from '../../../hooks/useSwipeDismiss';
import { useFocusTrap } from '../../../hooks/useFocusTrap';
import { saveEventDraft, loadEventDraft, clearEventDraft } from '../../../utils/formDraft';
import { isHexColor } from '../../../utils/calendar/constants';

const MONTH_NAMES = ['January','February','March','April','May','June',
  'July','August','September','October','November','December'];
const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun',
  'Jul','Aug','Sep','Oct','Nov','Dec'];

// ─── Shared UI atoms ──────────────────────────────────────────────────────────

interface ChipProps {
  icon?: React.ReactNode;
  label?: string | null;
  active?: boolean;
  placeholder: string;
  onClick: () => void;
}
const MetaChip = React.forwardRef<HTMLButtonElement, ChipProps>(
  ({ icon, label, active, placeholder, onClick }, ref) => (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-lg text-xs transition-colors border ${
        active
          ? 'border-[var(--border-primary)] bg-[var(--surface-tertiary)] text-[var(--content-secondary)]'
          : 'border-transparent text-[var(--content-tertiary)] hover:border-[var(--border-secondary)] hover:bg-[var(--surface-tertiary)] hover:text-[var(--content-secondary)]'
      }`}
    >
      {icon}
      <span>{active && label ? label : placeholder}</span>
    </button>
  )
);

interface PopoverProps {
  style: React.CSSProperties;
  width?: number;
  children: React.ReactNode;
  onClose: () => void;
  ignoreRef: React.RefObject<HTMLElement | null>;
}
const Popover: React.FC<PopoverProps> = ({ style, width = 200, children, onClose, ignoreRef }) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => {
      const t = e.target as Node;
      const onPortal = !!(t as Element).closest?.('[data-tag-input-portal]');
      if (!ref.current?.contains(t) && !ignoreRef.current?.contains(t) && !onPortal) onClose();
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [onClose, ignoreRef]);

  return createPortal(
    <div
      ref={ref}
      data-tag-input-portal
      style={{ ...style, width, boxShadow: 'var(--shadow-lg)' }}
      className="fixed bg-[var(--surface-elevated)] border border-[var(--border-primary)] rounded-xl z-[500] animate-slideDown overflow-hidden"
    >
      {children}
    </div>,
    document.body
  );
};

const Toggle: React.FC<{ checked: boolean; onChange: () => void }> = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={onChange}
    className={`relative w-9 h-5 rounded-full transition-colors flex-shrink-0 ${checked ? 'bg-[var(--accent-primary)]' : 'bg-[var(--border-primary)]'}`}
  >
    <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-4' : 'translate-x-0'}`} />
  </button>
);


// ─── Props ────────────────────────────────────────────────────────────────────

type ActivePopover = 'calendar' | 'date' | 'tags' | null;

interface EventFormModalProps {
  isOpen: boolean;
  position: { x: number; y: number; barRect?: DOMRect; centerHorizontally?: boolean } | null;

  eventTitle: string;
  eventNotes: string;
  startDate: string;
  endDate: string;
  selectedCalendar: Calendar | null;
  allDay: boolean;
  fullSize: boolean;
  startTime: string;
  endTime: string;
  recurrenceRule: RecurrenceRule;
  editingEvent: CalendarEvent | null;

  onTitleChange: (title: string) => void;
  onNotesChange: (notes: string) => void;
  onStartDateChange: (date: string) => void;
  onEndDateChange: (date: string) => void;
  onCalendarChange: (tag: Calendar | null) => void;
  onAllDayChange: (allDay: boolean) => void;
  onFullSizeChange: (fullSize: boolean) => void;
  onStartTimeChange: (time: string) => void;
  onEndTimeChange: (time: string) => void;
  onRecurrenceChange: (rule: RecurrenceRule) => void;
  onSelectedCellsChange: (cells: string[]) => void;
  onPreviewColorChange: (color: string | null) => void;

  onSave: () => Promise<void>;
  onDelete: () => void;
  onClose: () => void;

  /** Viewer in a shared space: show the event, offer no way to change it. */
  readOnly?: boolean;
  restoreFromDraftOnOpen?: boolean;
  formSource?: 'header' | 'calendar' | 'board';

  googleCalendarConnected?: boolean;
  googleCalendarColor?: string;
  selectedGoogleCalendarId?: string | null;
  onGoogleCalendarChange?: (id: string | null) => void;

  contentTags?: Tag[];
  selectedContentTags?: Tag[];
  onContentTagsChange?: (tags: Tag[]) => void;
  onCreateContentTag?: (name: string) => Promise<Tag | null>;

  // Legacy — kept for backwards compat
  kanbanColumns?: KanbanColumn[];
  onCreateTodo?: (data: {
    title: string; notes: string | null; column_id: string; due_date: string | null;
    due_time: string | null; calendar_id: string | null; priority: 'none' | 'low' | 'medium' | 'high';
  }) => Promise<void>;
}

// ─── Component ────────────────────────────────────────────────────────────────

export const EventFormModal: React.FC<EventFormModalProps> = ({
  isOpen,
  position,
  eventTitle,
  eventNotes,
  startDate,
  endDate,
  selectedCalendar,
  allDay,
  fullSize,
  startTime,
  endTime,
  recurrenceRule,
  editingEvent,
  onTitleChange,
  onNotesChange,
  onStartDateChange,
  onEndDateChange,
  onCalendarChange,
  onAllDayChange,
  onStartTimeChange,
  onEndTimeChange,
  onRecurrenceChange,
  onSelectedCellsChange,
  onPreviewColorChange,
  onSave,
  onDelete,
  onClose,
  restoreFromDraftOnOpen = false,
  readOnly = false,
  contentTags = [],
  selectedContentTags = [],
  onContentTagsChange,
  onCreateContentTag,
  googleCalendarConnected,
  googleCalendarColor = 'bg-blue-500',
  selectedGoogleCalendarId,
  onGoogleCalendarChange,
}) => {
  const { calendars } = useCalendar();
  const modalRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalRef, isOpen);
  const { onTouchStart: swipeTouchStart, onTouchMove: swipeTouchMove, onTouchEnd: swipeTouchEnd } = useSwipeDismiss(onClose, modalRef);

  // Snapshot for draft saving
  const formSnapshotRef = useRef({ eventTitle, eventNotes, startDate, endDate, startTime, endTime, selectedCalendar, allDay, fullSize, recurrenceRule });
  useEffect(() => {
    formSnapshotRef.current = { eventTitle, eventNotes, startDate, endDate, startTime, endTime, selectedCalendar, allDay, fullSize, recurrenceRule };
  });

  // Dragging
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [modalPosition, setModalPosition] = useState<{ left: number; top: number } | null>(null);
  useEffect(() => { if (isOpen) setModalPosition(null); }, [isOpen, position]);

  // Popover state
  const [activePopover, setActivePopover] = useState<ActivePopover>(null);
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});
  const calendarBtnRef = useRef<HTMLButtonElement>(null);
  const dateBtnRef = useRef<HTMLButtonElement>(null);
  const tagsBtnRef = useRef<HTMLButtonElement>(null);

  // Date popover state
  const [selectionPhase, setSelectionPhase] = useState<'start' | 'end'>('start');
  const [hoverDate, setHoverDate] = useState<string | null>(null);
  const [calViewDate, setCalViewDate] = useState<Date>(() => new Date());

  // Tags popover state
  const [tagQuery, setTagQuery] = useState('');
  const [creatingTag, setCreatingTag] = useState(false);

  // Form UI state
  const [showValidation, setShowValidation] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  // Reset on modal open
  useEffect(() => {
    if (!isOpen) return;
    setActivePopover(null);
    setSelectionPhase('start');
    setHoverDate(null);
    setShowValidation(false);
    setConfirmingDelete(false);
    setTagQuery('');
    setCalViewDate(startDate ? new Date(startDate + 'T12:00:00') : new Date());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Draft restore
  useEffect(() => {
    if (!isOpen || !restoreFromDraftOnOpen || editingEvent) return;
    const draft = loadEventDraft();
    if (!draft?.title) return;
    onTitleChange(draft.title);
    if (draft.notes) onNotesChange(draft.notes);
    if (draft.startDate) onStartDateChange(draft.startDate);
    if (draft.endDate) onEndDateChange(draft.endDate);
    if (draft.startTime) onStartTimeChange(draft.startTime);
    if (draft.endTime) onEndTimeChange(draft.endTime);
    if (draft.allDay !== undefined) onAllDayChange(draft.allDay);
    if (draft.recurrenceRule) onRecurrenceChange(draft.recurrenceRule);
    if (draft.tagId) {
      const tag = calendars.find(t => t.id === draft.tagId);
      if (tag) { onCalendarChange(tag); onPreviewColorChange(tag.color); }
    }
    if (draft.startDate && draft.endDate) onSelectedCellsChange(dateRangeToSelectedCells(draft.startDate, draft.endDate));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, restoreFromDraftOnOpen]);

  // handleDismissRef — always-current, guards draft on real content only
  const handleDismissRef = useRef(() => {});
  handleDismissRef.current = () => {
    if (!editingEvent) {
      const v = formSnapshotRef.current;
      const hasContent = v.eventTitle.trim() || v.eventNotes.trim() || v.startDate || v.recurrenceRule.frequency !== 'none';
      if (hasContent) {
        saveEventDraft({
          title: v.eventTitle, notes: v.eventNotes, startDate: v.startDate, endDate: v.endDate,
          startTime: v.startTime, endTime: v.endTime, tagId: v.selectedCalendar?.id ?? null,
          allDay: v.allDay, fullSize: v.fullSize, recurrenceRule: v.recurrenceRule,
        });
      }
    }
    onClose();
  };

  // Escape: close popover first, then modal
  const activePopoverRef = useRef<ActivePopover>(null);
  activePopoverRef.current = activePopover;
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        e.stopPropagation();
        if (activePopoverRef.current) {
          setActivePopover(null);
          setTagQuery('');
        } else {
          handleDismissRef.current();
        }
      }
      const activeEl = document.activeElement as HTMLElement | null;
      // Enter-to-save would otherwise slip past both the inert body and the
      // hidden footer button.
      if (e.key === 'Enter' && !readOnly && !e.shiftKey && isOpen && activeEl?.tagName !== 'TEXTAREA' && !activeEl?.closest?.('[data-tag-input-container]')) {
        e.preventDefault();
        handleValidatedSaveRef.current();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  // readOnly is a dependency: the handler closes over it, and this effect
  // suppresses exhaustive-deps, so it would otherwise be captured stale.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, readOnly]);

  // handleValidatedSave via ref to avoid stale closure in keydown listener
  const handleValidatedSaveRef = useRef(async () => {});

  // Drag
  const isInteractiveElement = useCallback((target: HTMLElement): boolean => {
    const selectors = ['input','textarea','button','select','a','[role="button"]','[role="combobox"]','[contenteditable]'];
    let el: HTMLElement | null = target;
    while (el && el !== modalRef.current) {
      if (selectors.some(s => el!.matches(s))) return true;
      if (el.closest('[data-no-drag]')) return true;
      el = el.parentElement;
    }
    return false;
  }, []);

  const handleDragStart = useCallback((e: React.MouseEvent) => {
    if (!modalRef.current || isInteractiveElement(e.target as HTMLElement)) return;
    const rect = modalRef.current.getBoundingClientRect();
    setDragOffset({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    setIsDragging(true);
    e.preventDefault();
  }, [isInteractiveElement]);

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => {
      const p = 10, mw = 400, mh = modalRef.current?.offsetHeight || 450;
      setModalPosition({
        left: Math.max(p, Math.min(e.clientX - dragOffset.x, window.innerWidth - mw - p)),
        top: Math.max(p, Math.min(e.clientY - dragOffset.y, window.innerHeight - mh - p)),
      });
    };
    const onUp = () => setIsDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => { window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
  }, [isDragging, dragOffset]);

  const timeFormat = useTimeFormat();

  if (!isOpen) return null;

  // ── Popover helpers ────────────────────────────────────────────────────────

  const openPopover = (type: ActivePopover, btnRef: React.RefObject<HTMLButtonElement | null>) => {
    if (activePopover === type) { setActivePopover(null); setTagQuery(''); return; }
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const popW = type === 'date' ? 288 : 200;
    const goUp = window.innerHeight - rect.bottom < (type === 'date' ? 480 : 320);
    setPopoverStyle({
      position: 'fixed',
      left: Math.min(rect.left, window.innerWidth - popW - 8),
      ...(goUp ? { bottom: window.innerHeight - rect.top + 4 } : { top: rect.bottom + 6 }),
    });
    setActivePopover(type);
  };

  // ── Derived values ─────────────────────────────────────────────────────────

  const isGoogleCalendarSelected = !!selectedGoogleCalendarId;
  const calendarLabel = isGoogleCalendarSelected ? 'Google Calendar' : (selectedCalendar?.name ?? null);
  const calendarActive = isGoogleCalendarSelected || !!selectedCalendar;
  const calendarDotColor = isGoogleCalendarSelected ? googleCalendarColor : (selectedCalendar?.color || 'bg-unassigned');

  const formatDateChip = (): string => {
    if (!startDate) return 'Set date';
    const [sy, sm, sd] = startDate.split('-').map(Number);
    const fmt = (d: Date) => `${MONTH_SHORT[d.getMonth()]} ${d.getDate()}`;
    const startD = new Date(sy, sm - 1, sd);
    if (!endDate || endDate === startDate) {
      if (!allDay && startTime) return `${fmt(startD)} · ${startTime}`;
      return fmt(startD);
    }
    const [ey, em, ed] = endDate.split('-').map(Number);
    return `${fmt(startD)} – ${fmt(new Date(ey, em - 1, ed))}`;
  };
  const dateActive = !!startDate;
  const dateLabel = dateActive ? formatDateChip() : null;

  const trimmedTagQuery = tagQuery.trim().toLowerCase();
  const filteredTags = contentTags.filter(t => t.name.toLowerCase().includes(trimmedTagQuery));
  const exactTagMatch = contentTags.some(t => t.name.toLowerCase() === trimmedTagQuery);
  const showCreateTag = trimmedTagQuery.length >= 2 && !exactTagMatch;

  const showRepeat = recurrenceRule.frequency !== 'none';

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleBackdropMouseDown = (e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    const onTagPortal = !!target.closest('[data-tag-input-portal]');
    if (modalRef.current && !modalRef.current.contains(target) && !onTagPortal) {
      handleDismissRef.current();
    }
  };

  const handleValidatedSave = async () => {
    if (!eventTitle.trim()) { setShowValidation(true); return; }
    setShowValidation(false);
    await onSave();
    clearEventDraft();
  };
  handleValidatedSaveRef.current = handleValidatedSave;

  const handleStartDateInput = (value: string) => {
    onStartDateChange(value);
    if (!endDate) onEndDateChange(value);
    if (value && endDate) onSelectedCellsChange(dateRangeToSelectedCells(value, endDate));
    if (value) setCalViewDate(new Date(value + 'T12:00:00'));
  };
  const handleEndDateInput = (value: string) => {
    onEndDateChange(value);
    if (startDate && value) onSelectedCellsChange(dateRangeToSelectedCells(startDate, value));
  };

  const handleTagSelect = (tag: Calendar | null) => {
    onCalendarChange(tag);
    onPreviewColorChange(tag?.color || 'bg-unassigned');
    onGoogleCalendarChange?.(null);
    setActivePopover(null);
  };
  const handleGoogleCalendarSelect = () => {
    onGoogleCalendarChange?.('primary');
    onCalendarChange(null);
    onPreviewColorChange(googleCalendarColor);
    setActivePopover(null);
  };

  const handleCreateTag = async () => {
    if (!trimmedTagQuery || creatingTag) return;
    setCreatingTag(true);
    try {
      const t = await onCreateContentTag?.(trimmedTagQuery);
      if (t) { onContentTagsChange?.([...selectedContentTags, t]); setTagQuery(''); }
    } finally { setCreatingTag(false); }
  };

  // ── Mini calendar (range) ──────────────────────────────────────────────────

  const renderMiniCalendar = () => {
    const year = calViewDate.getFullYear();
    const month = calViewDate.getMonth();
    const DAY_NAMES = ['Mo','Tu','We','Th','Fr','Sa','Su'];
    const firstDayOfWeek = (new Date(year, month, 1).getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const todayStr = new Date().toISOString().split('T')[0];

    const cells: (string | null)[] = [];
    for (let i = 0; i < firstDayOfWeek; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(`${year}-${String(month + 1).padStart(2,'0')}-${String(d).padStart(2,'0')}`);
    }

    const effectiveEnd = (selectionPhase === 'end' && hoverDate) ? hoverDate : endDate;
    const lo = startDate && effectiveEnd ? (startDate <= effectiveEnd ? startDate : effectiveEnd) : null;
    const hi = startDate && effectiveEnd ? (startDate <= effectiveEnd ? effectiveEnd : startDate) : null;

    const handleDayClick = (dateStr: string) => {
      if (selectionPhase === 'start') {
        onStartDateChange(dateStr);
        onEndDateChange(dateStr);
        onSelectedCellsChange(dateRangeToSelectedCells(dateStr, dateStr));
        setSelectionPhase('end');
      } else {
        const newStart = dateStr < startDate ? dateStr : startDate;
        const newEnd = dateStr < startDate ? startDate : dateStr;
        onStartDateChange(newStart);
        onEndDateChange(newEnd);
        onSelectedCellsChange(dateRangeToSelectedCells(newStart, newEnd));
        setSelectionPhase('start');
        setHoverDate(null);
      }
    };

    return (
      <div className="select-none">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold text-[var(--content-primary)]">{MONTH_NAMES[month]} {year}</span>
          <div className="flex items-center gap-0.5">
            <button type="button" onClick={() => setCalViewDate(new Date(year, month - 1, 1))}
              className="w-6 h-6 flex items-center justify-center rounded hover:bg-[var(--surface-tertiary)] text-[var(--content-secondary)] transition-colors">
              <ChevronLeft className="w-3.5 h-3.5" />
            </button>
            <button type="button" onClick={() => setCalViewDate(new Date())} title="Today"
              className="w-6 h-6 flex items-center justify-center rounded hover:bg-[var(--surface-tertiary)] text-[var(--content-tertiary)] transition-colors text-[11px] leading-none">
              ●
            </button>
            <button type="button" onClick={() => setCalViewDate(new Date(year, month + 1, 1))}
              className="w-6 h-6 flex items-center justify-center rounded hover:bg-[var(--surface-tertiary)] text-[var(--content-secondary)] transition-colors">
              <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-7 mb-1">
          {DAY_NAMES.map(d => <div key={d} className="h-6 flex items-center justify-center text-[10px] font-medium text-[var(--content-tertiary)]">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((dateStr, i) => {
            if (!dateStr) return <div key={`e-${i}`} className="h-7" />;
            const isStart = dateStr === startDate;
            const isEnd = dateStr === effectiveEnd && !!effectiveEnd;
            const inRange = lo && hi && dateStr > lo && dateStr < hi;
            const isSelected = isStart || (isEnd && effectiveEnd !== startDate);
            const isToday = dateStr === todayStr;
            const dayNum = parseInt(dateStr.split('-')[2]);

            let cls = 'h-7 w-full flex items-center justify-center text-[11px] cursor-pointer transition-colors ';
            if (isSelected) {
              cls += 'bg-[var(--accent-primary)] text-white font-semibold rounded-full ';
            } else if (inRange) {
              cls += 'bg-[var(--accent-subtle)] text-[var(--content-primary)]';
            } else {
              cls += 'rounded-full text-[var(--content-primary)] hover:bg-[var(--surface-tertiary)]';
            }
            if (isToday && !isSelected) cls += ' font-bold';

            return (
              <button key={dateStr} type="button" className={cls}
                onClick={() => handleDayClick(dateStr)}
                onMouseEnter={() => selectionPhase === 'end' && setHoverDate(dateStr)}
                onMouseLeave={() => setHoverDate(null)}
              >
                {dayNum}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // ── Modal positioning ──────────────────────────────────────────────────────

  const calculateModalStyle = (): React.CSSProperties => {
    const isMobile = window.innerWidth < 480;
    const mw = isMobile ? window.innerWidth - 16 : 400;
    const mwPx = `${mw}px`;
    if (isMobile) return { left: 0, right: 0, bottom: 0, width: '100%', maxHeight: '85dvh', overflowY: 'auto', borderRadius: '16px 16px 0 0' };
    if (modalPosition) return { left: `${modalPosition.left}px`, top: `${modalPosition.top}px`, width: mwPx, maxHeight: `${window.innerHeight - modalPosition.top - 20}px`, overflowY: 'auto' };
    if (!position) return { left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: mwPx, maxHeight: `${window.innerHeight - 40}px` };

    const gap = 15, padding = 20, vh = window.innerHeight, vw = window.innerWidth;
    let left = 0, anchorY = 0;
    if (position.centerHorizontally) { left = (vw - mw) / 2; anchorY = position.y - 50; }
    else if (position.barRect) {
      const br = position.barRect;
      if (vw - br.right >= mw + gap + padding) left = br.right + gap;
      else if (br.left >= mw + gap + padding) left = br.left - mw - gap;
      else left = Math.max(padding, (vw - mw) / 2);
      anchorY = br.top;
    } else { left = position.x; anchorY = position.y; }

    left = Math.max(padding, Math.min(left, vw - mw - padding));
    const spaceBelow = vh - anchorY - padding, spaceAbove = anchorY - padding;
    if (spaceBelow >= spaceAbove) {
      const top = Math.max(padding, anchorY);
      return { left: `${left}px`, top: `${top}px`, width: mwPx, maxHeight: `${Math.max(300, vh - top - padding)}px` };
    }
    return { left: `${left}px`, bottom: `${Math.max(padding, vh - Math.min(anchorY, vh - padding))}px`, width: mwPx, maxHeight: `${Math.max(300, spaceAbove)}px` };
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div
      className="fixed inset-0 z-[60] bg-[var(--modal-overlay)] animate-fadeInBackdrop"
      onMouseDown={handleBackdropMouseDown}
    >
      <div
        ref={modalRef}
        data-event-modal
        role="dialog"
        aria-modal="true"
        className={`absolute bg-[var(--modal-bg)] flex flex-col border border-[var(--border-primary)] ${
          window.innerWidth < 480 ? 'animate-slideUp' : 'animate-fadeIn rounded-2xl overflow-hidden'
        } ${isDragging ? 'cursor-grabbing select-none' : 'cursor-default'}`}
        style={{ ...calculateModalStyle(), boxShadow: 'var(--shadow-lg)' }}
        onMouseDown={window.innerWidth >= 480 ? handleDragStart : undefined}
      >
        {/* Mobile swipe handle */}
        <div className="sm:hidden flex justify-center pt-2 pb-1 cursor-grab"
          onTouchStart={swipeTouchStart} onTouchMove={swipeTouchMove} onTouchEnd={swipeTouchEnd}>
          <div className="w-10 h-1 rounded-full bg-[var(--content-tertiary)] opacity-40" />
        </div>

        {/* Close button */}
        <button
          onClick={() => handleDismissRef.current()}
          className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center rounded-lg text-[var(--content-tertiary)] hover:text-[var(--content-secondary)] hover:bg-[var(--surface-tertiary)] transition-colors z-10"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Body */}
        <div
          className="px-5 pt-5 pb-4 overflow-y-auto overflow-x-visible scrollbar-styled flex-1 min-h-0"
          style={{ scrollbarGutter: 'stable' }}
          /* Viewers see the event but cannot touch any field. `inert` also
             removes the subtree from the tab order, which a scatter of
             `disabled` props would not do reliably across the custom
             date/time/tag controls. */
          inert={readOnly}
        >
          {/* Title */}
          <input
            type="text"
            value={eventTitle}
            onChange={(e) => {
              const v = e.target.value;
              onTitleChange(v.length === 1 ? v.toUpperCase() : v);
              if (showValidation && v.trim()) setShowValidation(false);
            }}
            className={`w-full bg-transparent text-[var(--content-primary)] placeholder-[var(--content-tertiary)] text-xl font-bold tracking-tight focus:outline-none pb-1 pr-8 ${
              showValidation && !eventTitle.trim() ? 'border-b border-[var(--error)]' : ''
            }`}
            placeholder={editingEvent ? 'Event name' : 'New event'}
            maxLength={200}
            autoFocus={window.innerWidth > 768}
            aria-invalid={showValidation && !eventTitle.trim()}
            aria-label="Event title"
          />

          {/* Chips row */}
          <div className="flex flex-wrap gap-1 mt-2 -ml-1">
            {/* Calendar chip */}
            <MetaChip
              ref={calendarBtnRef}
              icon={calendarActive ? (
                isHexColor(calendarDotColor)
                  ? <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: calendarDotColor }} />
                  : <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${calendarDotColor}`} />
              ) : undefined}
              label={calendarLabel}
              active={calendarActive}
              placeholder="Calendar"
              onClick={() => openPopover('calendar', calendarBtnRef)}
            />

            {/* Date chip */}
            <MetaChip
              ref={dateBtnRef}
              label={dateLabel}
              active={dateActive}
              placeholder="Set date"
              onClick={() => openPopover('date', dateBtnRef)}
            />

            {/* Content tag chips */}
            {onContentTagsChange && (
              <>
                {selectedContentTags.map(tag => (
                  <span key={tag.id} className="inline-flex items-center gap-1 h-7 px-2.5 rounded-lg text-xs border border-[var(--border-primary)] bg-[var(--surface-tertiary)] text-[var(--content-secondary)]">
                    <TagIcon className="w-3.5 h-3.5 flex-shrink-0 text-[var(--content-tertiary)]" />
                    {tag.name}
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); onContentTagsChange(selectedContentTags.filter(t => t.id !== tag.id)); }}
                      className="ml-0.5 hover:text-[var(--content-primary)] transition-colors"
                      aria-label={`Remove tag ${tag.name}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
                <MetaChip
                  ref={tagsBtnRef}
                  icon={<TagIcon className="w-3.5 h-3.5 flex-shrink-0" />}
                  label={null}
                  active={false}
                  placeholder={selectedContentTags.length > 0 ? 'Add tag' : 'Tags'}
                  onClick={() => openPopover('tags', tagsBtnRef)}
                />
              </>
            )}
          </div>

          {/* Notes */}
          <textarea
            value={eventNotes}
            onChange={(e) => onNotesChange(e.target.value)}
            className="w-full mt-4 bg-transparent text-[var(--content-primary)] placeholder-[var(--content-tertiary)] text-[13px] focus:outline-none resize-none leading-relaxed"
            placeholder="Add notes…"
            maxLength={5000}
            rows={3}
          />

          {/* Footer */}
          {readOnly ? (
            <div className="flex items-center gap-2 mt-4 pt-3 border-t border-[var(--border-secondary)]">
              <Eye className="w-3.5 h-3.5 text-[var(--content-tertiary)]" />
              <span className="text-xs text-[var(--content-tertiary)]">
                View-only access to this space
              </span>
            </div>
          ) : (
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-[var(--border-secondary)]">
            {editingEvent ? (
              confirmingDelete ? (
                <div className="flex items-center gap-2">
                  <button onClick={onDelete} className="text-xs text-[var(--status-error-text)] hover:underline transition-colors">Confirm</button>
                  <button onClick={() => setConfirmingDelete(false)} className="text-xs text-[var(--content-tertiary)] hover:text-[var(--content-secondary)] transition-colors">Cancel</button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmingDelete(true)}
                  title="Delete event"
                  className="w-7 h-7 flex items-center justify-center rounded-lg text-[var(--content-tertiary)] hover:text-[var(--status-error-text)] hover:bg-[var(--status-error-bg)] transition-colors"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )
            ) : <div />}

            <button
              onClick={handleValidatedSave}
              disabled={!eventTitle.trim()}
              className="btn-primary !w-auto px-4 py-1.5 text-xs disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {editingEvent ? 'Save' : 'Add event'}
            </button>
          </div>
          )}
        </div>
      </div>

      {/* ── Calendar popover ──────────────────────────────────────────────── */}
      {activePopover === 'calendar' && (
        <Popover
          style={popoverStyle}
          width={200}
          onClose={() => setActivePopover(null)}
          ignoreRef={calendarBtnRef as React.RefObject<HTMLElement | null>}
        >
          {calendars.map((tag) => (
            <button
              key={tag.id}
              type="button"
              onClick={() => handleTagSelect(tag)}
              className={`w-full px-3 py-1.5 text-xs text-left flex items-center gap-2 hover:bg-[var(--sidebar-hover)] transition-colors text-[var(--content-primary)] ${selectedCalendar?.id === tag.id ? 'bg-[var(--accent-subtle)]' : ''}`}
            >
              {isHexColor(tag.color)
                ? <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: tag.color }} />
                : <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${tag.color}`} />}
              <span className="flex-1">{tag.name}</span>
              {selectedCalendar?.id === tag.id && <Check className="w-3 h-3 text-[var(--accent-primary)] flex-shrink-0" />}
            </button>
          ))}
          {googleCalendarConnected && (
            <>
              <div className="border-t border-[var(--border-secondary)] my-1" />
              <div className="px-3 py-1">
                <span className="text-[10px] font-medium text-[var(--content-tertiary)] uppercase tracking-wider">Google</span>
              </div>
              <button
                type="button"
                onClick={handleGoogleCalendarSelect}
                className={`w-full px-3 py-1.5 text-xs text-left flex items-center gap-2 hover:bg-[var(--sidebar-hover)] transition-colors text-[var(--content-primary)] ${isGoogleCalendarSelected ? 'bg-[var(--accent-subtle)]' : ''}`}
              >
                {isHexColor(googleCalendarColor)
                  ? <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: googleCalendarColor }} />
                  : <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${googleCalendarColor}`} />}
                <span className="flex-1">Google Calendar</span>
                {isGoogleCalendarSelected && <Check className="w-3 h-3 text-[var(--accent-primary)] flex-shrink-0" />}
              </button>
            </>
          )}
        </Popover>
      )}

      {/* ── Date popover ──────────────────────────────────────────────────── */}
      {activePopover === 'date' && (
        <Popover
          style={popoverStyle}
          width={296}
          onClose={() => { setActivePopover(null); }}
          ignoreRef={dateBtnRef as React.RefObject<HTMLElement | null>}
        >
          <div className="p-4">
            {/* Mini range calendar */}
            <div className="mb-1" data-no-drag>{renderMiniCalendar()}</div>

            {/* All day toggle */}
            <div className="flex items-center justify-between py-2.5 border-t border-[var(--border-secondary)] mt-2">
              <span className="text-xs text-[var(--content-secondary)]">All day</span>
              <Toggle checked={allDay} onChange={() => onAllDayChange(!allDay)} />
            </div>

            {/* Starts row */}
            <div className="flex items-center gap-2 py-2.5" data-no-drag>
              <span className="text-xs text-[var(--content-secondary)] w-10 flex-shrink-0">Starts</span>
              <DateTextInput
                value={startDate}
                onChange={handleStartDateInput}
                onFocus={() => setSelectionPhase('start')}
              />
              {!allDay && (
                <InlineTimeInput value={startTime} onChange={onStartTimeChange} timeFormat={timeFormat} />
              )}
            </div>

            {/* Ends row */}
            <div className="flex items-center gap-2 py-2.5" data-no-drag>
              <span className="text-xs text-[var(--content-secondary)] w-10 flex-shrink-0">Ends</span>
              <DateTextInput
                value={endDate}
                onChange={handleEndDateInput}
                onFocus={() => setSelectionPhase('end')}
              />
              {!allDay && (
                <EndTimeInput
                  value={endTime}
                  startTime={startTime}
                  onChange={onEndTimeChange}
                  onNextDay={() => {
                    const next = new Date((endDate || startDate) + 'T12:00:00');
                    next.setDate(next.getDate() + 1);
                    const nextStr = next.toISOString().split('T')[0];
                    onEndDateChange(nextStr);
                    if (startDate) onSelectedCellsChange(dateRangeToSelectedCells(startDate, nextStr));
                  }}
                  timeFormat={timeFormat}
                />
              )}
            </div>

            {/* Repeat section */}
            <div className="border-t border-[var(--border-secondary)] pt-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-[var(--content-secondary)]">Repeat</span>
                <Toggle
                  checked={showRepeat}
                  onChange={() => onRecurrenceChange(showRepeat ? { frequency: 'none', interval: 1 } : { frequency: 'weekly', interval: 1 })}
                />
              </div>

              {showRepeat && (
                <div className="space-y-2" data-no-drag>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[var(--content-tertiary)] w-10 flex-shrink-0">Every</span>
                    <input
                      type="number"
                      min={1}
                      max={99}
                      value={recurrenceRule.interval}
                      onChange={(e) => onRecurrenceChange({ ...recurrenceRule, interval: Math.max(1, Math.min(99, parseInt(e.target.value) || 1)) })}
                      className="w-12 px-2.5 py-1.5 bg-[var(--input-bg)] rounded-lg text-xs text-[var(--content-primary)] text-center focus:outline-none focus:ring-1 focus:ring-[var(--accent-primary)]"
                    />
                    <select
                      value={recurrenceRule.frequency}
                      onChange={(e) => onRecurrenceChange({ ...recurrenceRule, frequency: e.target.value as RecurrenceRule['frequency'] })}
                      className="flex-1 px-2.5 py-1.5 bg-[var(--input-bg)] rounded-lg text-xs text-[var(--content-primary)] focus:outline-none focus:ring-1 focus:ring-[var(--accent-primary)]"
                    >
                      <option value="daily">Days</option>
                      <option value="weekly">Weeks</option>
                      <option value="monthly">Months</option>
                      <option value="yearly">Years</option>
                    </select>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[var(--content-tertiary)] w-10 flex-shrink-0">Ends</span>
                    <div className="flex-1">
                      <DatePicker
                        value={recurrenceRule.endDate || ''}
                        onChange={(val) => onRecurrenceChange({ ...recurrenceRule, endDate: val || undefined })}
                        min={endDate || startDate}
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Popover>
      )}

      {/* ── Tags popover ──────────────────────────────────────────────────── */}
      {activePopover === 'tags' && onContentTagsChange && (
        <Popover
          style={popoverStyle}
          width={200}
          onClose={() => { setActivePopover(null); setTagQuery(''); }}
          ignoreRef={tagsBtnRef as React.RefObject<HTMLElement | null>}
        >
          <div className="p-1.5">
            <input
              type="text"
              value={tagQuery}
              onChange={(e) => setTagQuery(e.target.value)}
              placeholder="Search tags…"
              autoFocus
              data-tag-input-container
              className="w-full px-2 py-1 text-xs bg-[var(--surface-secondary)] rounded-lg text-[var(--content-primary)] placeholder-[var(--content-tertiary)] focus:outline-none"
              onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (filteredTags.length > 0) {
                    const tag = filteredTags[0];
                    const isSel = selectedContentTags.some(t => t.id === tag.id);
                    onContentTagsChange(isSel ? selectedContentTags.filter(t => t.id !== tag.id) : [...selectedContentTags, tag]);
                  } else if (showCreateTag) handleCreateTag();
                }
                if (e.key === 'Escape') { setActivePopover(null); setTagQuery(''); }
              }}
            />
          </div>

          {filteredTags.length === 0 && !showCreateTag && (
            <p className="px-3 py-1.5 text-xs text-[var(--content-tertiary)]">{tagQuery.trim() ? 'No matches' : 'No tags yet'}</p>
          )}

          {filteredTags.map(tag => {
            const isSel = selectedContentTags.some(t => t.id === tag.id);
            return (
              <button key={tag.id} type="button"
                onClick={() => onContentTagsChange(isSel ? selectedContentTags.filter(t => t.id !== tag.id) : [...selectedContentTags, tag])}
                className="w-full px-3 py-1.5 text-xs text-left flex items-center gap-1.5 hover:bg-[var(--sidebar-hover)] transition-colors text-[var(--content-primary)]"
              >
                <TagIcon className="w-3 h-3 text-[var(--content-tertiary)] flex-shrink-0" />
                <span className="flex-1">{tag.name}</span>
                <div className={`w-3.5 h-3.5 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                  isSel ? 'bg-[var(--accent-primary)] border-[var(--accent-primary)]' : 'border-[var(--content-tertiary)]'
                }`}>
                  {isSel && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                </div>
              </button>
            );
          })}

          {showCreateTag && (
            <button type="button" onClick={handleCreateTag} disabled={creatingTag}
              className="w-full px-3 py-1.5 text-xs text-left flex items-center gap-1.5 hover:bg-[var(--sidebar-hover)] text-[var(--accent-primary)] border-t border-[var(--border-secondary)] disabled:opacity-50"
            >
              <Plus className="w-3 h-3 flex-shrink-0" />
              {creatingTag ? 'Adding…' : `Add "${trimmedTagQuery}"`}
            </button>
          )}
        </Popover>
      )}
    </div>
  );
};
